import React, { useCallback, useEffect, useMemo } from 'react';
import Countdown, { zeroPad } from 'react-countdown-now';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router';
import { ENetworkRequestStatus } from 'services/BackendApi';
import { requestOtpEmailRequestAction, setOtpCodeAction, verifyOtpEmailRequestAction } from 'store/modules/otp/actions';
import { VerifyOtpResponseMessages } from 'store/modules/otp/model';
import { otpCodeSelector, otpEmailSelector, resendOtpTimerSelector, sendOtpEmailRequestStatusSelector, verifyOtpErrorsSelector, verifyOtpRequestStatusSelector } from 'store/modules/otp/selectors';
import FluidButton from 'ui/FluidButton';
import { TextInput } from 'ui/TextInput';

const VerifyOtp: React.FC = () => {
  const dispatch = useDispatch();
  const email = useSelector(otpEmailSelector);
  const otp = useSelector(otpCodeSelector);
  const resendOtpTimer = useSelector(resendOtpTimerSelector);
  const resendOtpRequestStatus = useSelector(sendOtpEmailRequestStatusSelector);
  const verifyOtpRequestStatus = useSelector(verifyOtpRequestStatusSelector);
  const verifyOtpErrors = useSelector(verifyOtpErrorsSelector);
  const history = useHistory();
  const match = useRouteMatch<{ itemUuid: string }>();
  const { itemUuid } = match.params;
  const errorMessage = verifyOtpErrors && verifyOtpErrors[0] === VerifyOtpResponseMessages.INVALID_OTP ? 'Password is not valid' : null;

  const handleReSendOtp = useCallback(() => {
    dispatch(requestOtpEmailRequestAction(itemUuid, history));
  }, [dispatch]);

  const handleOtpChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => dispatch(setOtpCodeAction(e.target.value)), [dispatch]);

  const countDownRenderer = useCallback(({ minutes, seconds, completed }) => {
    const isResendEnabled = completed && resendOtpRequestStatus !== ENetworkRequestStatus.PENDING;
    return (
      <div className={`text-[15px] underline mr-auto ${isResendEnabled ? 'text-brown-prime cursor-pointer' : 'text-gray-100 select-none'}`} onClick={() => isResendEnabled && handleReSendOtp()}>
        {completed
          ? 'Resend OTP'
          : <>Resend OTP in <span>{zeroPad(minutes)}:{zeroPad(seconds)}</span> seconds</>
        }
      </div>
    );
  }, [resendOtpTimer, resendOtpRequestStatus]);

  const handleSendOtp = useCallback(() => {
    if (email && otp) {
      dispatch(verifyOtpEmailRequestAction(itemUuid, history, email, otp));
    }
  }, [dispatch, email, otp, itemUuid, history]);

  useEffect(() => {
    if (!email) {
      history.replace(`/otp/${itemUuid}${history.location.search}`);
    }
  }, [email, history, itemUuid]);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        handleSendOtp();
      }}
      className="w-[360px] min-h-[373px] max-h-[385px] px-[35px] py-6 font-pt-sans bg-white rounded shadow-[0px_0px_1px_0px_rgba(15,23,42,0.06)] flex flex-col justify-start items-center gap-6"
    >
      <div className="justify-start text-black text-xl font-normal font-noe-display">One Time Password</div>
      <div className="self-stretch justify-center text-black text-base font-normal font-pt-sans leading-snug">
        Please, enter and verify the OTP sent to {email}.
      </div>
      <label className="text-black text-base font-bold font-pt-sans w-full self-stretch">
        Your OTP
        <TextInput
          id="otp"
          value={otp}
          onChange={handleOtpChange}
          type="text"
          maxLength={6}
          errorClassName='font-light mb-[3px]'
          errorMessage={errorMessage}
        />
        <div className='text-gray-100 text-[14px] font-normal'>If you don’t see the email, please check your Spam folder.</div>
      </label>
      <Countdown key={resendOtpTimer} date={resendOtpTimer} renderer={countDownRenderer} />
      <FluidButton
        type='primary'
        className='flex-1 self-stretch'
        onClick={handleSendOtp}
        disabled={otp.length < 6}
        isLoading={verifyOtpRequestStatus === ENetworkRequestStatus.PENDING}
        submit
      >
        Verify
      </FluidButton>
    </form>
  );
};

export default VerifyOtp;